"use client";

import React from "react";
import JtbdHero from "./JtbdHero";
import JtbdContentRow from "./JtbdContentRow";
import FreePfpTray from "./FreePfpTray";
import { StyleCard, LookCard } from "./JtbdCards";
import CatalogToolsGrid from "@/components/dashboard/catalog/CatalogToolsGrid";
import type { LandingDemoId } from "@/libs/landingHandoff";
import type { FreePfpHandoffSource } from "./freePfpAnalytics";
import {
  getCuratedAvatarProfessional,
  getCuratedAvatarViral,
  getHeadshotLookCards,
  AvatarStyle,
  avatarThumbnailUrl,
} from "./jtbdData";

export default function JtbdDashboardV2({
  showFreePfpTray = true,
  landingDemoId = null,
  landingPpUid = null,
  landingHandoffSource = null,
}: {
  showFreePfpTray?: boolean;
  landingDemoId?: LandingDemoId | null;
  landingPpUid?: string | null;
  landingHandoffSource?: FreePfpHandoffSource | null;
}) {
  const avatarPro = getCuratedAvatarProfessional();
  const avatarViral = getCuratedAvatarViral();
  const headshotLooks = getHeadshotLookCards();

  return (
    <div className={showFreePfpTray ? "space-y-5 md:space-y-12" : "space-y-12 md:space-y-16"}>
      {showFreePfpTray && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FreePfpTray
            landingDemoId={landingDemoId}
            landingPpUid={landingPpUid}
            landingHandoffSource={landingHandoffSource}
          />
        </div>
      )}

      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${showFreePfpTray ? "hidden md:block" : ""}`}>
        <JtbdHero />
      </div>

      {headshotLooks.length > 0 && (
        <div id="studio-looks" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <JtbdContentRow
            title="Headshot Looks"
            subtitle="Ready-made professional looks - pick one, add your selfie, done"
            viewAllHref="/editor-v2/ai-headshots"
            viewAllLabel="View all looks →"
            cardWidth="w-44 md:w-52"
          >
            {headshotLooks.map((look, i) => (
              <LookCard
                key={look.id}
                title={look.title}
                imageSrc={look.imageSrc}
                href={look.href}
                section="Headshot Looks"
                lookId={look.id}
                badge={i === 0 ? "Popular" : undefined}
              />
            ))}
          </JtbdContentRow>
        </div>
      )}

      {avatarPro.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <JtbdContentRow
            title="Professional & Personal Branding"
            subtitle="Command respect with a polished, on-brand look"
            viewAllHref="/editor-v2/ai-avatars"
            viewAllLabel="View all 197 →"
            cardWidth="w-36 md:w-64"
          >
            {avatarPro.map((style: AvatarStyle) => (
              <StyleCard
                key={style.id}
                title={style.title}
                imageSrc={avatarThumbnailUrl(style)}
                href={`/editor-v2/ai-avatars?style=${style.id}`}
                section="Professional & Personal Branding"
                cardType="style"
                cardId={String(style.id)}
                toolId="ai-avatars"
                aspect="1/1"
              />
            ))}
          </JtbdContentRow>
        </div>
      )}

      {avatarViral.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <JtbdContentRow
            title="Viral & Instagram"
            subtitle="Stand out with trending styles that get attention"
            viewAllHref="/editor-v2/ai-avatars"
            viewAllLabel="View all 197 →"
            cardWidth="w-36 md:w-64"
          >
            {avatarViral.map((style: AvatarStyle) => (
              <StyleCard
                key={style.id}
                title={style.title}
                imageSrc={avatarThumbnailUrl(style)}
                href={`/editor-v2/ai-avatars?style=${style.id}`}
                section="Viral & Instagram"
                cardType="style"
                cardId={String(style.id)}
                toolId="ai-avatars"
                aspect="1/1"
              />
            ))}
          </JtbdContentRow>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <CatalogToolsGrid />
      </div>
    </div>
  );
}
